import { SubmissionContext } from '@analyzer/contexts/submission-context'
import { AddDocumentDialog } from '@analyzer/features/submissions/$submissionId/components/AddDocumentDialog'
import { ContentWithSubmissionMenu } from '@analyzer/features/submissions/$submissionId/components/ContentWithSubmissionMenu'
import { SubmissionPendingScreen } from '@analyzer/features/submissions/$submissionId/components/SubmissionPendingScreen'
import { ROUTES } from '@analyzer/utils/router/paths'
import { usePatchAnalysisReviewUpdateStatus } from '@api/endpoints/analysis-reviews/patch-analysis-review-update-status'
import type { GroupVersionHistoryEntryPublic } from '@api/endpoints/analysis-reviews/post-analysis-review-group-version-history-schema'
import { usePostAnalysisReviewRerunGroupAction } from '@api/endpoints/analysis-reviews/post-analysis-review-rerun-group-action'
import { getDocumentQueryOptions } from '@api/endpoints/document/get-document'
import type { DocGroupPublic } from '@api/endpoints/workspaces/document-groups/get-workspace-document-group-schema'
import { usePostWorkspaceDocumentGroupReProcess } from '@api/endpoints/workspaces/document-groups/post-workspace-document-group-re-process'
import { getMinimapGroups } from '@api/review/getMinimapGroups'
import { parseReviewComponents } from '@api/review/parseReviewComponents'
import {
  type ReviewPublicFull,
  type ReviewStatus,
  ReviewStatuses,
} from '@api/review/schema'
import {
  type DocGroupState,
  useDocGroupState,
} from '@core/hooks/useDocGroupState'
import { DeclineReviewButton } from '@design/components/decline-review-button/DeclineReviewButton'
import { ReviewHistorySelect } from '@design/components/form/selects/review-history-select/ReviewHistorySelect'
import { Content } from '@design/components/layouts/content/Content'
import { ContentWithAppHeader } from '@design/components/layouts/content-with-app-header/ContentWithAppHeader'
import { ContentWithPdf } from '@design/components/layouts/content-with-pdf/ContentWithPdf'
import { Skeleton } from '@design/components/loaders/skeleton/Skeleton'
import { Spinner } from '@design/components/loaders/spinner/Spinner'
import { EmptyScreen } from '@design/components/screens/empty-screen/EmptyScreen'
import { ErrorScreen } from '@design/components/screens/error-screen/ErrorScreen'
import { ValidateReviewButton } from '@design/components/validate-review-button/ValidateReviewButton'
import { useAttributesStore } from '@design/stores/attributes-store'
import { useBreadcrumbStore } from '@design/stores/breadcrumb-store'
import { useQueries } from '@tanstack/react-query'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { CircleX, FileText, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface SubmissionSearch {
  reviewId?: string
}

export const Route = createFileRoute(
  '/_authenticated/$workspaceId/submissions/$submissionId'
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): SubmissionSearch => ({
    reviewId: typeof search.reviewId === 'string' ? search.reviewId : undefined,
  }),
})

function RouteComponent() {
  const { workspaceId, submissionId } = Route.useParams()
  const { reviewId } = Route.useSearch()

  const state = useDocGroupState(workspaceId, submissionId, reviewId)

  if (state.kind === 'loading') return <ReviewLoading />

  if (state.kind === 'error') return <ReviewError />

  if (state.kind === 'no-documents') {
    return <ReviewEmpty docGroup={state.docGroup} />
  }

  return (
    <DocGroupLoaded
      workspaceId={workspaceId}
      submissionId={submissionId}
      state={state}
    />
  )
}

function ReviewLoading() {
  const { t } = useTranslation()

  useEffect(() => {
    useBreadcrumbStore
      .getState()
      .setBreadcrumbs([
        { label: t('nav.breadcrumbs.submissions'), href: ROUTES.submissions },
        { label: <Spinner className='size-3' /> },
      ])
  }, [t])

  return (
    <ContentWithAppHeader>
      <Content className='p-8'>
        <Skeleton className='mb-4 h-8 w-64' />
        <Skeleton className='h-64 w-full rounded-md' />
      </Content>
    </ContentWithAppHeader>
  )
}

function ReviewError() {
  const { t } = useTranslation()

  useEffect(() => {
    useBreadcrumbStore
      .getState()
      .setBreadcrumbs([
        { label: t('nav.breadcrumbs.submissions'), href: ROUTES.submissions },
        { label: <CircleX className='size-3 text-destructive' /> },
      ])
  }, [t])

  return (
    <ContentWithAppHeader>
      <ErrorScreen message={t('submissions.loadingSubmissionFailed')} />
    </ContentWithAppHeader>
  )
}

function ReviewEmpty({ docGroup }: { docGroup: DocGroupPublic }) {
  const { t } = useTranslation()
  const [isUploadDialogOpen, setUploadDialogOpen] = useState(false)

  useEffect(() => {
    useBreadcrumbStore
      .getState()
      .setBreadcrumbs([
        { label: t('nav.breadcrumbs.submissions'), href: ROUTES.submissions },
        { label: docGroup.name },
      ])
  }, [t, docGroup.name])

  return (
    <ContentWithAppHeader>
      <EmptyScreen
        className='mt-24 mb-auto'
        icon={<FileText />}
        title={t('submissions.files.emptyTitle')}
        description={t('submissions.files.emptyDescription')}
        action={{
          label: t('submissions.files.add'),
          icon: <Plus className='size-4' />,
          onClick: () => setUploadDialogOpen(true),
        }}
      />

      <AddDocumentDialog
        open={isUploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        lockedGroup={docGroup}
      />
    </ContentWithAppHeader>
  )
}

type DocGroupLoadedProps = {
  workspaceId: string
  submissionId: string
  state: Exclude<DocGroupState, { kind: 'loading' | 'error' | 'no-documents' }>
}

function DocGroupLoaded({
  state,
  workspaceId,
  submissionId,
}: DocGroupLoadedProps) {
  // --- Re-Process callback ---
  const { mutateAsync: reprocessMutate } =
    usePostWorkspaceDocumentGroupReProcess()

  const reprocessGroup = useCallback(
    (mode: 'failed' | 'all') =>
      reprocessMutate({
        params: { workspace_id: workspaceId, group_id: submissionId },
        payload: { shouldReprocessGraph: false, analysisReprocess: mode },
      }),
    [reprocessMutate, workspaceId, submissionId]
  )

  if (state.kind !== 'ready') {
    return (
      <SubmissionPendingScreen
        docGroup={state.docGroup}
        workspaceId={workspaceId}
        reprocessGroup={reprocessGroup}
      />
    )
  }

  return (
    <ReviewFullyLoaded
      workspaceId={workspaceId}
      submissionId={submissionId}
      docGroup={state.docGroup}
      review={state.review}
      versions={state.versions}
      reprocessGroup={reprocessGroup}
    />
  )
}

type ReviewFullyLoadedProps = {
  workspaceId: string
  submissionId: string
  docGroup: DocGroupPublic
  review: ReviewPublicFull
  versions: GroupVersionHistoryEntryPublic[]
  reprocessGroup: (mode: 'failed' | 'all') => Promise<unknown>
}

function ReviewFullyLoaded({
  workspaceId,
  submissionId,
  docGroup,
  review,
  versions,
  reprocessGroup,
}: ReviewFullyLoadedProps) {
  const navigate = useNavigate()

  const [isUploadDialogOpen, toggleUploadDialogOpen] = useState(false)

  const reviewId = review.id

  // --- Rerun callback ---
  const { mutateAsync: rerunMutate } = usePostAnalysisReviewRerunGroupAction()

  const rerunGroupAction = useCallback(
    () => rerunMutate({ workspace_id: workspaceId, review_id: reviewId }),
    [workspaceId, reviewId, rerunMutate]
  )

  // --- Loading documents ---
  const documentQueries = useQueries({
    queries: (docGroup?.documentIds ?? []).map((docId) =>
      getDocumentQueryOptions({
        workspace_id: workspaceId,
        document_id: docId,
        includeTextExtraction: true,
      })
    ),
  })

  const documents = useMemo(
    () => documentQueries.flatMap((q) => (q.data ? [q.data] : [])),
    [documentQueries]
  )

  const textExtractions = useMemo(() => {
    const result: Record<
      string,
      NonNullable<(typeof documents)[0]['textExtraction']>
    > = {}
    for (const doc of documents) {
      if (doc.textExtraction) result[doc.id] = doc.textExtraction
    }
    return result
  }, [documents])

  // Every component's blocks as a gray overview on the minimap; a click
  // highlights them and opens the matching attribute (group id = component id).
  const minimapGroups = useMemo(
    () => getMinimapGroups(review.components),
    [review.components]
  )

  function openAttribute(componentId: string) {
    navigate({
      to: ROUTES.submissionAttribute,
      params: { workspaceId, submissionId, componentId },
    })
  }

  // --- Update Review Status ---
  const {
    mutateAsync: updateReviewStatus,
    isPending: isUpdateReviewPending,
    error: updateReviewStatusError,
  } = usePatchAnalysisReviewUpdateStatus()

  const updateCurrentReviewStatus = useCallback(
    (status: ReviewStatus) =>
      updateReviewStatus({
        params: { workspace_id: workspaceId, review_id: reviewId },
        payload: { status },
      }),
    [workspaceId, reviewId, updateReviewStatus]
  )

  /**
   * So this useMemo is actually just because I needed to update the stores in a timely fashion
   * useEffect will only trigger after the component renders which is too late. I need it immediately.
   * And neither ReactRouter nor ReactQuery provides efficient ways of reacting to the data change.
   *
   * NOTE: In the future this should be reworked as a createStore pattern where the store is then passed around with a context
   */
  useMemo(() => {
    const { components, id, groupActionResults, groupActionDefinitions } =
      review

    const { componentMap, componentByComponentResult } =
      parseReviewComponents(components)

    useAttributesStore.getState().init({
      componentMap,
      componentByComponentResult,
      // "Verifications" controls are not shown in the submission attribute view on purpose (PRO-1786)
      controls: [],
      id,
      groupActionDefinitions,
      groupActionResults,
    })
  }, [review])

  const isLoadingDocuments = documentQueries.some((q) => q.isLoading)
  const hasDocumentsErrors = documentQueries.some((q) => q.isError)
  const isReviewReadOnly = review.status !== ReviewStatuses.IN_PROGRESS

  return (
    <SubmissionContext.Provider
      value={{
        docGroup,
        review,
        documents,
        isLoadingDocuments,
        hasDocumentsErrors,
        isReviewReadOnly,
        toggleUploadDialogOpen,
        rerunGroupAction,
        reprocessGroup,
        updateCurrentReviewStatus,
      }}
    >
      <ContentWithPdf
        documents={documents}
        textExtractions={textExtractions}
        minimapGroups={minimapGroups}
        onGroupClick={openAttribute}
      >
        <ContentWithAppHeader
          headerAfter={
            versions.length > 0 && (
              <>
                <ReviewHistorySelect
                  versions={versions}
                  reviewId={reviewId}
                  scoreField='complianceRate'
                  onHistoryChange={(id) =>
                    navigate({
                      to: '.',
                      from: Route.fullPath,
                      search: (prev) => ({ ...prev, reviewId: id }),
                    })
                  }
                  className='ms-auto w-min'
                />

                {!isReviewReadOnly && (
                  <>
                    <DeclineReviewButton
                      isLoading={isUpdateReviewPending}
                      hasError={!!updateReviewStatusError}
                      onDecline={() =>
                        updateCurrentReviewStatus(ReviewStatuses.DECLINED)
                      }
                    />

                    <ValidateReviewButton
                      isLoading={isUpdateReviewPending}
                      hasError={!!updateReviewStatusError}
                      onValidate={() =>
                        updateCurrentReviewStatus(ReviewStatuses.VALID)
                      }
                    />
                  </>
                )}
              </>
            )
          }
        >
          <ContentWithSubmissionMenu reviewId={reviewId}>
            <Outlet />
          </ContentWithSubmissionMenu>
        </ContentWithAppHeader>
      </ContentWithPdf>

      <AddDocumentDialog
        open={isUploadDialogOpen}
        onOpenChange={toggleUploadDialogOpen}
        lockedGroup={docGroup}
      />
    </SubmissionContext.Provider>
  )
}
